import React, { useTransition } from "react";
import { useCurrentUser } from "@/hooks/useProfileData";
import { loggerService } from "@/services/logger";
import CookieManager from "@/utils/CookieManager";
import { DynamicsAddress } from "@/types/dynamics.types";

export interface SimpleOrganization {
  id: string;
  name: string;
}

export interface FullOrganization extends SimpleOrganization {
  id: string;
  name: string;
  email?: string;
  telephone1?: string;
  telephone2?: string;
  businessId?: string;
  address?: DynamicsAddress | null;
}

interface SelectedAccountContextValue {
  companies: FullOrganization[];
  selectedOrganization: FullOrganization | null;
  selectedOrganizationId: string | null;
  setSelectedOrganizationId: (id: string | null) => void;
  isLoading: boolean;
}

const SelectedAccountContext =
  React.createContext<SelectedAccountContextValue | null>(null);
const STORAGE_KEY = "selectedOrganizationId";
const normalizeOrganizationId = (id: string | null | undefined): string =>
  (id ?? "")
    .trim()
    .replace(/["{}]/g, "")
    .toLowerCase();

export const SelectedAccountProvider: React.FC<React.PropsWithChildren<{}>> = ({
  children,
}) => {
  const { data: currentUser, isLoading } = useCurrentUser();

  // 1. Initial State: Read immediately from cookie so UI doesn't flicker/reset
  const [selectedOrganizationId, setSelectedOrganizationIdState] =
    React.useState<string | null>(() => {
      return CookieManager.get(STORAGE_KEY);
    });
  const [_isPending, startTransition] = useTransition();

  // 2. Map Organizations from API
  const companies = React.useMemo<FullOrganization[]>(() => {
    if (!currentUser?.organizations) return [];
    return currentUser.organizations.map((org) => ({
      id: org.account.id,
      name: org.account.name,
      email: org.account.email,
      telephone1: org.account.telephone1,
      telephone2: org.account.telephone2,
      businessId: org.account.businessId,
      address: org.account.address || null,
    }));
  }, [currentUser]);

  // 3. Validation Effect: Only runs once data is finished loading
  React.useEffect(() => {
    // DO NOT run logic while loading; this protects the cookie from being wiped
    if (isLoading || !currentUser) return;

    const storedId = CookieManager.get(STORAGE_KEY);
    const normalizedStoredId = normalizeOrganizationId(storedId);

    if (companies.length > 0) {
      const isStoredValid = companies.some(
        (c) => normalizeOrganizationId(c.id) === normalizedStoredId,
      );

      if (isStoredValid && storedId) {
        // Cookie is valid, sync state
        startTransition(() => setSelectedOrganizationIdState(storedId));
      } else {
        // Cookie invalid/missing: Default to first and update cookie
        const firstId = companies[0].id;
        startTransition(() => setSelectedOrganizationIdState(firstId));
        CookieManager.set(STORAGE_KEY, firstId, { days: 7 });
      }
    } else {
      // Confirmed 0 companies: Clear
      startTransition(() => setSelectedOrganizationIdState(null));
      if (CookieManager.exists(STORAGE_KEY)) {
        CookieManager.delete(STORAGE_KEY);
      }
    }
  }, [companies, isLoading, currentUser, startTransition]);

  const setSelectedOrganizationId = React.useCallback((id: string | null) => {
    if (id !== null && (typeof id !== "string" || id.trim() === "")) return;

    startTransition(() => setSelectedOrganizationIdState(id));
    try {
      if (id) {
        CookieManager.set(STORAGE_KEY, id, { days: 7 });
      } else {
        CookieManager.delete(STORAGE_KEY);
      }
    } catch (error) {
      loggerService.trackException(
        error instanceof Error ? error : new Error(String(error)),
      );
    }
  }, []);

  const selectedOrganization = React.useMemo(() => {
    const normalizedSelectedId = normalizeOrganizationId(selectedOrganizationId);
    return (
      companies.find(
        (c) => normalizeOrganizationId(c.id) === normalizedSelectedId,
      ) ?? null
    );
  }, [companies, selectedOrganizationId]);

  return (
    <SelectedAccountContext.Provider
      value={{
        companies,
        selectedOrganization,
        selectedOrganizationId,
        setSelectedOrganizationId,
        isLoading,
      }}
    >
      {children}
    </SelectedAccountContext.Provider>
  );
};

export function useSelectedAccount() {
  const ctx = React.useContext(SelectedAccountContext);
  if (!ctx)
    throw new Error(
      "useSelectedAccount must be used within SelectedAccountProvider",
    );

  const selectedAccountId =
    ctx.selectedOrganization?.id ?? ctx.selectedOrganizationId;

  return { ...ctx, selectedAccountId };
}
